import * as React from "react"
import { useEffect, useRef } from "react"
import Joyride from "react-joyride"

import { TOUR_TRANSLATIONS } from "../@constants"
import { useTour } from "../@hooks"
import { tourLanguage, tourDashboardIsReady, resetTourSignals, dashboardHasLoaded } from "../@signals"
import { isLoadingCommonFilters } from "@signals/filters/common"
import TourTooltip from "./TourTooltip"

export default function TourProvider({ pageKey }) {
    const { steps, isRunning, stepIndex, handleJoyrideCallback } = useTour(pageKey)
    const t = TOUR_TRANSLATIONS[tourLanguage.value]
    const prevLoadingRef = useRef(null)
    const tourStartedRef = useRef(false)
    const isSettledRef = useRef(false)
    const prevGlobalLoadingRef = useRef(null)

    // Leer signals en el render para suscribirse a cambios
    const currentLoading = isLoadingCommonFilters.value

    // Mount: reset y esperar a que se asienten las limpiezas de navegación
    useEffect(() => {
        dashboardHasLoaded.value = false
        const id = setTimeout(() => {
            isSettledRef.current = true
            prevGlobalLoadingRef.current = isLoadingCommonFilters.value
        }, 0)
        return () => {
            clearTimeout(id)
            resetTourSignals()
            dashboardHasLoaded.value = false
            isSettledRef.current = false
        }
    }, [])

    // Track global: si isLoadingCommonFilters pasa de true a false, el dashboard ya tiene datos
    // Ignora transiciones antes de que el componente se asiente (limpiezas de navegación)
    useEffect(() => {
        if (!isSettledRef.current) return
        if (prevGlobalLoadingRef.current === true && currentLoading === false) {
            dashboardHasLoaded.value = true
        }
        prevGlobalLoadingRef.current = currentLoading
    }, [currentLoading])

    // Monitorear inicio del tour
    useEffect(() => {
        if (isRunning && !tourStartedRef.current) {
            tourStartedRef.current = true
            prevLoadingRef.current = null
        }

        if (!isRunning) {
            tourStartedRef.current = false
            prevLoadingRef.current = null
        }
    }, [isRunning])

    // Monitorear cambios en isLoadingCommonFilters para el tour activo
    useEffect(() => {
        if (tourStartedRef.current && isRunning) {
            if (prevLoadingRef.current === true && currentLoading === false) {
                tourDashboardIsReady.value = true
            }
            prevLoadingRef.current = currentLoading
        }
    }, [currentLoading, isRunning])

    return (
        <Joyride
            steps={steps}
            run={isRunning && tourDashboardIsReady.value}
            stepIndex={stepIndex}
            continuous
            showSkipButton
            showProgress
            scrollToFirstStep
            disableOverlayClose
            callback={handleJoyrideCallback}
            tooltipComponent={TourTooltip}
            locale={{
                next: t.next,
                back: t.back,
                skip: t.skip,
                last: t.finish,
            }}
            styles={{
                options: {
                    zIndex: 10000,
                },
                overlay: {
                    backgroundColor: "rgba(0, 0, 0, 0.35)",
                },
                spotlight: {
                    borderRadius: 12,
                },
            }}
            floaterProps={{
                styles: {
                    arrow: {
                        length: 8,
                        spread: 14,
                    },
                },
                disableAnimation: true,
            }}
        />
    )
}
